import { Colors } from "@/enums/Colors";
import Tenant from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/Tenant";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { Debouncer } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/Debouncer";
import Check from "@/images/checked.png";
import LazoCompanyService from "@/services/LazoCompanyService";
import { ExternalId } from "@/types/ExternalId";
import { Box, ListItem, ListItemButton, MenuList, SxProps } from "@mui/material";
import React, { Dispatch, SetStateAction, useMemo, useState } from "react";
import CompanyIcon from "../../../assets/images/default-company-avatar.png";
import { PortalFilters } from "../communications-chat-components/portal/PortalChatContext";
import LoadingDotsAnimated from "../utils/LoadingDotsAnimated";
import SearchField from "../utils/SearchField";

type Props = {
  selectedExternalIds: ExternalId[];
  setFilters: Dispatch<SetStateAction<PortalFilters>>;
  setTenantEntities?: Dispatch<SetStateAction<Tenant[]>>;
};

const debouncer = new Debouncer(0);

export const CompaniesFilterComponents = React.memo(function ({ setFilters, selectedExternalIds, setTenantEntities }: Props) {
  const [isSearchEmpty, setIsSearchEmpty] = useState<boolean>(true);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [filteredTenants, setFilteredTenants] = useState<Tenant[]>(tenants);
  const [loading, setLoading] = useState<boolean>(true);

  function handleItemClick(tenant: Tenant) {
    const selectedIdx = selectedExternalIds.indexOf(tenant.externalId);

    let newArray = [...selectedExternalIds];
    if (selectedIdx === -1) {
      newArray.push(tenant.externalId);
    } else {
      newArray.splice(selectedIdx, 1);
    }

    setFilters((prev) => ({ ...prev, tenantExternalIds: newArray ? newArray : [] }));

    // For display
    setTenantEntities?.((prev) => {
      const foundEntityIdx = prev.findIndex((t) => t.externalId === tenant.externalId);
      if (foundEntityIdx !== -1) {
        return prev.filter((p) => p.externalId !== tenant.externalId);
      }
      return prev.concat(tenant);
    });
  }

  function fetchCompaniesTenantData() {
    LazoCompanyService.getCompaniesTenantData()
      .then((response) => {
        response.onSuccess((t) => {
          setTenants(t);
        });
        response.onError((m) => {
          NotificationService.error(m);
        });
      })
      .finally(() => setLoading(false));
  }

  useMemo(() => fetchCompaniesTenantData(), []);

  function onSearch(searchRef: React.RefObject<HTMLInputElement>) {
    let searchValue = searchRef.current?.value || "";

    if (searchValue === "" || searchValue.length === 0) {
      setIsSearchEmpty(true);
      return;
    }
    setIsSearchEmpty(false);
    let filteredCompanies = tenants.filter((company) => {
      return company.name.toLowerCase().includes(searchValue.toLowerCase());
    });
    setFilteredTenants(filteredCompanies);
  }

  function onAllClick() {
    setFilters((prev) => ({ ...prev, tenantExternalIds: [] }));
  }

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "0.5rem",
        border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
        minHeight: "600px",
        height: "700px",
        maxHeight: "800px",
        overflowY: "hidden",
        backgroundColor: "#fff",
      }}
    >
      <Box sx={{ padding: "0", width: "100%", height: "100%", maxHeight: "100%" }}>
        <SearchField
          sx={{
            "& .MuiInputBase-root": {
              color: Colors.BLACK,
            },
            "& .MuiInputBase-input-MuiOutlinedInput-input": {
              color: Colors.BLACK,
            },
            "& .Mui-focused": {
              border: `1px solid ${Colors.LAZO_PURPLE}`,
            },
            width: "100%",
            display: "flex",
            padding: "0.5rem",
          }}
          iconVariant
          placeholder="Search"
          debouncer={debouncer}
          handleSearch={onSearch}
          displaySearchButton={false}
          triggerSearchOnInputChange={true}
        />
        <ListItemButton onClick={onAllClick} sx={{ borderBottom: "1px solid #535353" }}>
          All
        </ListItemButton>
        <Box sx={{ minHeight: "85%", maxHeight: "85%", overflow: "hidden", overflowY: "auto" }}>
          {loading && (
            <Box display="flex" alignItems="center" justifyContent="center" sx={{ padding: "2rem" }}>
              <LoadingDotsAnimated />
            </Box>
          )}
          {!loading && (
            <MenuList>
              {(isSearchEmpty ? tenants : filteredTenants).map((tenant, _) => (
                <ListItem
                  key={tenant.externalId}
                  sx={{
                    fontSize: ".75rem",
                    fontWeight: 500,
                    lineHeight: "1.125rem",
                    width: "100%",
                  }}
                >
                  <img
                    src={CompanyIcon.src}
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "4px",
                      backgroundColor: Colors.WHITE,
                    }}
                  />
                  <ListItemButton onClick={() => handleItemClick(tenant)} sx={ListItemButtonSx}>
                    {/* here goes avatar img, but we dont have avatarField */}

                    {tenant.name}
                    {selectedExternalIds.includes(tenant.externalId) ? (
                      <img src={Check.src} style={{ width: "20px", height: "20px" }} />
                    ) : (
                      <></>
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </MenuList>
          )}
        </Box>
      </Box>
    </Box>
  );
});

const ListItemButtonSx: SxProps = {
  display: "flex",
  justifyContent: "space-between",
  borderRadius: "0.5rem",
  ":hover": {
    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
  },
};

export default CompaniesFilterComponents;
